// Reglas puras de `clientCompany` (plan.md §2, TS puro — sin React/axios/MUI, constitution.md
// #2). Extraídas literal de `ClientCompaniesTab.jsx` (spec.md §5).

import type { ValidationResult } from './enterprise.types';
import type {
  ClientCompany,
  ClientCompanyFormValues,
  ClientCompanyStatus,
  DatacenterAssociationFormValues,
  DatacenterKind,
} from './clientCompany.types';
import type { DatacenterOption } from './options.types';

// UC-9 (`ClientCompaniesTab.jsx:118-126`): precarga con fallback `|| ''`, `status || 'active'`.
export function toClientCompanyFormValues(c: ClientCompany): ClientCompanyFormValues {
  return {
    name: c.name || '',
    nit: c.nit || '',
    description: c.description || '',
    status: c.status || 'active',
  };
}

// UC-9 (`:129-133`): `name` requerido.
export function validateClientCompany(
  form: ClientCompanyFormValues
): ValidationResult<'nameRequired'> {
  if (!form.name.trim()) {
    return { ok: false, error: 'nameRequired' };
  }
  return { ok: true };
}

// UC-8 (`:305`, `:320-321`): chip Estado, 'active' ⇒ success, cualquier otro valor ⇒ default.
export function clientCompanyStatusColor(status: ClientCompanyStatus): 'success' | 'default' {
  return status === 'active' ? 'success' : 'default';
}

// UC-8/UC-11 (`:478`): chip Tipo, 'kyndryl' ⇒ primary, 'client' ⇒ secondary.
export function datacenterKindColor(kind: DatacenterKind): 'primary' | 'secondary' {
  return kind === 'kyndryl' ? 'primary' : 'secondary';
}

// UC-11 (`:483`): ubicación = [city, country_name].filter(Boolean).join(', ') || '—'.
export function formatDatacenterLocation(city?: string | null, country?: string | null): string {
  return [city, country].filter(Boolean).join(', ') || '—';
}

// UC-11 (`:551`, `:573`): label de opción de DC `${name} — ${city || country_name}`.
export function datacenterOptionLabel(dc: DatacenterOption): string {
  return `${dc.name} — ${dc.city || dc.country_name}`;
}

// UC-11 (`:194-206`): valida según `kind` — kyndryl exige `datacenter_id`, client exige
// `client_datacenter_id`.
export function validateDatacenterAssociation(
  f: DatacenterAssociationFormValues
): ValidationResult<'datacenterRequired' | 'clientDatacenterRequired'> {
  if (f.kind === 'kyndryl') {
    if (!f.datacenter_id) {
      return { ok: false, error: 'datacenterRequired' };
    }
    return { ok: true };
  }
  if (!f.client_datacenter_id) {
    return { ok: false, error: 'clientDatacenterRequired' };
  }
  return { ok: true };
}

// UC-11 (`:192-206`): payload `{ notes: notes || null, ...id por kind }`.
export function toDatacenterAssociationPayload(
  f: DatacenterAssociationFormValues
): Record<string, unknown> {
  const payload: Record<string, unknown> = { notes: f.notes || null };
  if (f.kind === 'kyndryl') {
    payload.datacenter_id = f.datacenter_id;
  } else {
    payload.client_datacenter_id = f.client_datacenter_id;
  }
  return payload;
}
